import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserPlus, Mail, ArrowLeft, RefreshCw } from 'lucide-react';

const Register = () => {
  const [step, setStep] = useState(1); // 1 = form, 2 = OTP
  const [form, setForm] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    age: '', annualIncome: '', investmentExperience: 'BEGINNER',
  });
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { register, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const otpRefs = useRef([]);

  const validatePasswordPolicy = (password) => {
    const requirements = [];

    if (password.length < 8) {
      requirements.push('at least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      requirements.push('one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      requirements.push('one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      requirements.push('one digit');
    }

    return requirements;
  };

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordRequirements = validatePasswordPolicy(form.password);
    if (passwordRequirements.length > 0) {
      setError(`Password must include ${passwordRequirements.join(', ').replace(/, ([^,]*)$/, ' and $1')}`);
      return;
    }

    setLoading(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        age: Number(form.age) || undefined,
        annualIncome: Number(form.annualIncome) || undefined,
        investmentExperience: form.investmentExperience,
      });
      setStep(2);
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      await verifyOtp(form.email, otpString);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setSuccess('');
    try {
      await resendOtp(form.email);
      setSuccess('New OTP sent to your email!');
      setOtp(['', '', '', '', '', '']);
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    }
  };

  // Step 2: OTP Verification
  if (step === 2) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-8">
        <div className="card max-w-md w-full">
          <div className="text-center mb-6">
            <Mail className="h-10 w-10 text-primary-600 mx-auto mb-2" />
            <h2 className="text-2xl font-bold text-gray-900">Verify Your Email</h2>
            <p className="text-gray-500 text-sm mt-1">
              We've sent a 6-digit code to <span className="font-medium text-gray-700">{form.email}</span>
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-6">
            <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all"
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  autoFocus={i === 0}
                />
              ))}
            </div>

            <button type="submit" className="btn-primary w-full" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify & Continue'}
            </button>
          </form>

          <div className="text-center mt-4 space-y-2">
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="text-sm text-primary-600 hover:underline font-medium disabled:text-gray-400 disabled:no-underline inline-flex items-center gap-1"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
            </button>
            <br />
            <button
              onClick={() => { setStep(1); setError(''); setSuccess(''); }}
              className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to registration
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Registration Form
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-8">
      <div className="card max-w-md w-full">
        <div className="text-center mb-6">
          <UserPlus className="h-10 w-10 text-primary-600 mx-auto mb-2" />
          <h2 className="text-2xl font-bold text-gray-900">Create Account</h2>
          <p className="text-gray-500 text-sm mt-1">Start your smart investing journey</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Full Name</label>
            <input type="text" name="name" className="input-field" placeholder="John Doe"
              value={form.name} onChange={handleChange} required />
          </div>

          <div>
            <label className="label">Email</label>
            <input type="email" name="email" className="input-field" placeholder="you@example.com"
              value={form.email} onChange={handleChange} required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Password</label>
              <input type="password" name="password" className="input-field" placeholder="••••••••"
                value={form.password} onChange={handleChange} required minLength={8}
                onBlur={() => {
                  if (form.password) {
                    const passwordRequirements = validatePasswordPolicy(form.password);
                    if (passwordRequirements.length > 0) {
                      setError(`Password must include ${passwordRequirements.join(', ').replace(/, ([^,]*)$/, ' and $1')}`);
                    }
                  }
                }} />
              <p className="mt-1 text-xs text-gray-500">
                Use at least 8 characters with one uppercase letter, one lowercase letter, and one digit.
              </p>
            </div>
            <div>
              <label className="label">Confirm Password</label>
              <input type="password" name="confirmPassword" className="input-field" placeholder="••••••••"
                value={form.confirmPassword} onChange={handleChange} required minLength={8} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Age</label>
              <input type="number" name="age" className="input-field" placeholder="25"
                value={form.age} onChange={handleChange} min={18} max={100} />
            </div>
            <div>
              <label className="label">Annual Income (₹)</label>
              <input type="number" name="annualIncome" className="input-field" placeholder="500000"
                value={form.annualIncome} onChange={handleChange} min={0} />
            </div>
          </div>

          <div>
            <label className="label">Investment Experience</label>
            <select name="investmentExperience" className="input-field" value={form.investmentExperience} onChange={handleChange}>
              <option value="BEGINNER">Beginner</option>
              <option value="INTERMEDIATE">Intermediate</option>
              <option value="ADVANCED">Advanced</option>
            </select>
          </div>

          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? 'Sending OTP...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-4">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:underline font-medium">Login</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
